import { TransformControls } from '@react-three/drei';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type CameraControlsImpl from 'camera-controls';
import { useThree } from '@react-three/fiber';

import type { Skeleton } from '../../../../../lib/skeleton/core';
import type { MappingResult } from '../../../../../lib/recognize/config';
import {
  eulerDegreesFromQuaternion,
  normalizeQuaternion,
} from '../../../../../lib/skeleton/rotationService';
import { clampManipulatorScale } from '../../../shared';

interface JointOffsetManipulatorProps {
  skeleton: Skeleton;
  mapping: MappingResult | null;
  selectedBoneIndex: number | null;
  enabled: boolean;
  poseVersion: number;
  manipulatorScale: number;
  onJointOffsetChange?: (
    boneRef: string,
    rotation: [number, number, number] | null,
  ) => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

type DraggingChangedEvent = { value: boolean };

type TransformControlsHandle = {
  axis: string | null;
  addEventListener: (
    type: 'dragging-changed',
    listener: (event: DraggingChangedEvent) => void,
  ) => void;
  removeEventListener: (
    type: 'dragging-changed',
    listener: (event: DraggingChangedEvent) => void,
  ) => void;
  traverse: (callback: (object: THREE.Object3D) => void) => void;
};

type MaterialLike = THREE.Material & {
  color?: THREE.Color;
  opacity: number;
  transparent: boolean;
  visible: boolean;
};

type RaycastableObject = THREE.Object3D & {
  raycast?: (...args: unknown[]) => void;
};

type PendingOffsetUpdate = {
  boneRef: string;
  rotation: [number, number, number] | null;
};

const OFFSET_EPSILON_DEGREES = 0.01;
const ROTATE_GIZMO_BASE_SIZE = 0.72;
const RING_MARKER_POSITION_STEP = 1;
const RING_MARKER_DIAMETER = 0.05;
const RING_MARKER_ACTIVE_SCALE = 1.15;
const RING_MARKER_IDLE_OPACITY = 0.9;
const RING_MARKER_TEXTURE_SIZE = 64;
const RING_MARKER_TEXTURE_RADIUS_RATIO = 0.28;
const RING_MARKER_TEXTURE_STROKE_RATIO = 0.09;
const HANDLE_MARKER_DIAMETER = 0.15;
const HANDLE_MARKER_ACTIVE_SCALE = 1.12;
const HANDLE_MARKER_IDLE_OPACITY = 0.96;
const HANDLE_MARKER_TEXTURE_RADIUS_RATIO = 0.26;

function normalizeOffsetDegrees(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) < OFFSET_EPSILON_DEGREES) return 0;
  const wrapped = THREE.MathUtils.euclideanModulo(value + 180, 360) - 180;
  return Math.abs(wrapped) < OFFSET_EPSILON_DEGREES ? 0 : wrapped;
}

function readQuaternionFromMatrix(matrix: THREE.Matrix4): THREE.Quaternion {
  const quaternion = new THREE.Quaternion();
  matrix.decompose(new THREE.Vector3(), quaternion, new THREE.Vector3());
  return normalizeQuaternion(quaternion);
}

function sanitizeOffsetEuler(
  euler: [number, number, number],
): [number, number, number] | null {
  const next: [number, number, number] = [
    normalizeOffsetDegrees(euler[0]),
    normalizeOffsetDegrees(euler[1]),
    normalizeOffsetDegrees(euler[2]),
  ];
  return next.some((value) => Math.abs(value) >= OFFSET_EPSILON_DEGREES)
    ? next
    : null;
}


function buildCircleOutlineTexture(): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;

  const size = RING_MARKER_TEXTURE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) return null;

  context.clearRect(0, 0, size, size);
  context.beginPath();
  context.arc(
    size / 2,
    size / 2,
    size * RING_MARKER_TEXTURE_RADIUS_RATIO,
    0,
    Math.PI * 2,
  );
  context.lineWidth = size * RING_MARKER_TEXTURE_STROKE_RATIO;
  context.strokeStyle = '#ffffff';
  context.stroke();

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function buildFilledCircleTexture(): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;

  const size = RING_MARKER_TEXTURE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) return null;

  context.clearRect(0, 0, size, size);
  context.beginPath();
  context.arc(
    size / 2,
    size / 2,
    size * HANDLE_MARKER_TEXTURE_RADIUS_RATIO,
    0,
    Math.PI * 2,
  );
  context.fillStyle = '#ffffff';
  context.fill();

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function buildRingMarkerOverlay(
  lineGeometry: THREE.BufferGeometry,
  texture: THREE.Texture,
  color: THREE.Color,
): {
  group: THREE.Group;
  update: (isActive: boolean) => void;
  dispose: () => void;
} | null {
  const positions = lineGeometry.getAttribute('position');
  if (!positions || positions.count === 0) return null;

  const group = new THREE.Group();
  const materials: THREE.SpriteMaterial[] = [];
  const sprites: THREE.Sprite[] = [];

  for (
    let index = 0;
    index < positions.count;
    index += RING_MARKER_POSITION_STEP
  ) {
    const material = new THREE.SpriteMaterial({
      map: texture,
      color: color.clone(),
      transparent: true,
      opacity: RING_MARKER_IDLE_OPACITY,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      sizeAttenuation: true,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.set(
      positions.getX(index),
      positions.getY(index),
      positions.getZ(index),
    );
    sprite.scale.setScalar(RING_MARKER_DIAMETER);
    sprite.renderOrder = 1001;
    group.add(sprite);
    materials.push(material);
    sprites.push(sprite);
  }

  return {
    group,
    update: (isActive: boolean) => {
      for (let index = 0; index < materials.length; index += 1) {
        materials[index].opacity = isActive ? 1 : RING_MARKER_IDLE_OPACITY;
        sprites[index].scale.setScalar(
          isActive
            ? RING_MARKER_DIAMETER * RING_MARKER_ACTIVE_SCALE
            : RING_MARKER_DIAMETER,
        );
      }
    },
    dispose: () => {
      for (const material of materials) {
        material.dispose();
      }
    },
  };
}

function buildHandleMarkerOverlay(
  geometry: THREE.BufferGeometry,
  texture: THREE.Texture,
  color: THREE.Color,
): {
  sprite: THREE.Sprite;
  update: (isActive: boolean) => void;
  dispose: () => void;
} | null {
  geometry.computeBoundingSphere();
  const center = geometry.boundingSphere?.center;
  if (!center) return null;

  const material = new THREE.SpriteMaterial({
    map: texture,
    color,
    transparent: true,
    opacity: HANDLE_MARKER_IDLE_OPACITY,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    sizeAttenuation: true,
  });
  const sprite = new THREE.Sprite(material);
  sprite.position.copy(center);
  sprite.scale.setScalar(HANDLE_MARKER_DIAMETER);
  sprite.renderOrder = 1002;

  return {
    sprite,
    update: (isActive: boolean) => {
      material.opacity = isActive ? 1 : HANDLE_MARKER_IDLE_OPACITY;
      sprite.scale.setScalar(
        isActive
          ? HANDLE_MARKER_DIAMETER * HANDLE_MARKER_ACTIVE_SCALE
          : HANDLE_MARKER_DIAMETER,
      );
    },
    dispose: () => {
      material.dispose();
    },
  };
}

export default function JointOffsetManipulator({
  skeleton,
  mapping,
  selectedBoneIndex,
  enabled,
  poseVersion,
  manipulatorScale,
  onJointOffsetChange,
  onDraggingChange,
}: JointOffsetManipulatorProps) {
  const cameraControls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const [anchorObject, setAnchorObject] = useState<THREE.Group | null>(null);
  const [transformControls, setTransformControls] =
    useState<TransformControlsHandle | null>(null);
  const isDraggingRef = useRef(false);
  const queuedOffsetUpdateRef = useRef<PendingOffsetUpdate | null>(null);
  const flushRafRef = useRef<number | null>(null);
  const onJointOffsetChangeRef = useRef(onJointOffsetChange);
  const ringMarkerTexture = useMemo(() => buildCircleOutlineTexture(), []);
  const handleMarkerTexture = useMemo(() => buildFilledCircleTexture(), []);
  const hasSelection =
    selectedBoneIndex !== null &&
    selectedBoneIndex >= 0 &&
    selectedBoneIndex < skeleton.bones.length;
  const canShowGizmo = enabled && hasSelection && !!mapping;
  const neutralScale = useMemo(() => new THREE.Vector3(1, 1, 1), []);
  const safeManipulatorScale = clampManipulatorScale(manipulatorScale);

  useEffect(() => {
    onJointOffsetChangeRef.current = onJointOffsetChange;
  }, [onJointOffsetChange]);

  useEffect(
    () => () => {
      ringMarkerTexture?.dispose();
      handleMarkerTexture?.dispose();
    },
    [handleMarkerTexture, ringMarkerTexture],
  );

  const handleAnchorRef = useCallback((nextAnchor: THREE.Group | null) => {
    setAnchorObject((currentAnchor) =>
      currentAnchor === nextAnchor ? currentAnchor : nextAnchor,
    );
  }, []);

  const handleTransformControlsRef = useCallback((nextControls: unknown) => {
    const resolvedControls = nextControls as TransformControlsHandle | null;
    setTransformControls((currentControls) =>
      currentControls === resolvedControls ? currentControls : resolvedControls,
    );
  }, []);

  const flushQueuedUpdate = useCallback(() => {
    if (flushRafRef.current !== null) {
      cancelAnimationFrame(flushRafRef.current);
      flushRafRef.current = null;
    }
    const pending = queuedOffsetUpdateRef.current;
    queuedOffsetUpdateRef.current = null;
    if (!pending) return;
    onJointOffsetChangeRef.current?.(pending.boneRef, pending.rotation);
  }, []);

  const scheduleQueuedUpdate = useCallback(() => {
    if (flushRafRef.current !== null) return;
    flushRafRef.current = requestAnimationFrame(() => {
      flushRafRef.current = null;
      const pending = queuedOffsetUpdateRef.current;
      queuedOffsetUpdateRef.current = null;
      if (!pending) return;
      onJointOffsetChangeRef.current?.(pending.boneRef, pending.rotation);
    });
  }, []);

  const beginDragging = useCallback(() => {
    if (isDraggingRef.current) return;
    isDraggingRef.current = true;
    onDraggingChange?.(true);
    if (cameraControls) cameraControls.enabled = false;
  }, [cameraControls, onDraggingChange]);

  const endDragging = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    flushQueuedUpdate();
    onDraggingChange?.(false);
    if (cameraControls) cameraControls.enabled = true;
  }, [cameraControls, flushQueuedUpdate, onDraggingChange]);

  useEffect(() => {
    if (!canShowGizmo) return;
    if (!anchorObject || selectedBoneIndex === null) return;
    if (isDraggingRef.current) return;

    const selectedBone = skeleton.bone(selectedBoneIndex);
    selectedBone.posedWorld.decompose(
      anchorObject.position,
      anchorObject.quaternion,
      neutralScale,
    );
    anchorObject.scale.set(1, 1, 1);
    anchorObject.updateMatrixWorld(true);
  }, [
    anchorObject,
    canShowGizmo,
    neutralScale,
    poseVersion,
    selectedBoneIndex,
    skeleton,
  ]);

  useEffect(() => {
    if (canShowGizmo) return;
    endDragging();
  }, [canShowGizmo, endDragging]);

  useEffect(() => {
    if (!transformControls) return;

    const cleanupFns: Array<() => void> = [];
    transformControls.traverse((object) => {
      if (!(object.name === 'X' || object.name === 'Y' || object.name === 'Z')) {
        if (object.name === 'E') {
          const raycastable = object as RaycastableObject;
          const originalRaycast = raycastable.userData
            ._cartographerOriginalRaycast as RaycastableObject['raycast'] | undefined;
          if (!originalRaycast && raycastable.raycast) {
            raycastable.userData._cartographerOriginalRaycast = raycastable.raycast;
          }
          raycastable.raycast = () => {};
          const originalVisible = object.visible;
          object.visible = false;
          cleanupFns.push(() => {
            if (originalRaycast) {
              raycastable.raycast = originalRaycast;
            }
            object.visible = originalVisible;
          });
        }
        return;
      }

      const rawMaterial = (object as THREE.Object3D & {
        material?: THREE.Material | THREE.Material[];
      }).material;
      if (!rawMaterial || Array.isArray(rawMaterial)) return;

      const material = rawMaterial as MaterialLike;
      const lineObject = object as THREE.Line & { isLine?: boolean };
      const originalVisible = material.visible;
      const originalOnBeforeRender = object.onBeforeRender;

      if (lineObject.isLine && ringMarkerTexture && material.color) {
        const overlay = buildRingMarkerOverlay(
          lineObject.geometry,
          ringMarkerTexture,
          material.color.clone(),
        );
        if (!overlay) return;

        material.visible = false;
        lineObject.add(overlay.group);
        lineObject.onBeforeRender = () => {
          overlay.update(transformControls.axis === object.name);
        };

        cleanupFns.push(() => {
          lineObject.onBeforeRender = originalOnBeforeRender;
          lineObject.remove(overlay.group);
          overlay.dispose();
          material.visible = originalVisible;
        });
        return;
      }

      const meshObject = object as THREE.Mesh;
      if (!meshObject.isMesh || !handleMarkerTexture || !material.color) return;

      const overlay = buildHandleMarkerOverlay(
        meshObject.geometry,
        handleMarkerTexture,
        material.color.clone(),
      );
      if (!overlay) return;

      material.visible = false;
      meshObject.add(overlay.sprite);
      meshObject.onBeforeRender = () => {
        overlay.update(transformControls.axis === object.name);
      };

      cleanupFns.push(() => {
        meshObject.onBeforeRender = originalOnBeforeRender;
        meshObject.remove(overlay.sprite);
        overlay.dispose();
        material.visible = originalVisible;
      });
    });

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [handleMarkerTexture, ringMarkerTexture, transformControls]);

  useEffect(() => {
    if (!transformControls) return;

    const handleDraggingChanged = (event: DraggingChangedEvent) => {
      if (event.value) {
        beginDragging();
      } else {
        endDragging();
      }
    };

    transformControls.addEventListener(
      'dragging-changed',
      handleDraggingChanged,
    );
    return () => {
      transformControls.removeEventListener(
        'dragging-changed',
        handleDraggingChanged,
      );
    };
  }, [beginDragging, endDragging, transformControls]);

  useEffect(
    () => () => {
      endDragging();
    },
    [endDragging],
  );

  if (!canShowGizmo) return null;

  return (
    <>
      <group ref={handleAnchorRef} />
      {anchorObject && selectedBoneIndex !== null && (
        <TransformControls
          ref={handleTransformControlsRef}
          object={anchorObject}
          mode="rotate"
          space="local"
          size={ROTATE_GIZMO_BASE_SIZE * safeManipulatorScale}
          onObjectChange={() => {
            if (!isDraggingRef.current) return;
            if (selectedBoneIndex < 0 || selectedBoneIndex >= skeleton.bones.length) {
              return;
            }

            const bone = skeleton.bone(selectedBoneIndex);
            const nextWorldQuaternion = anchorObject.quaternion.clone();
            const parentWorldMatrix =
              bone.parent === null
                ? skeleton.rootParentWorld
                : skeleton.bone(bone.parent).posedWorld;
            const parentWorldQuaternion = readQuaternionFromMatrix(parentWorldMatrix);
            const restLocalQuaternion = readQuaternionFromMatrix(bone.restLocal);
            const combinedLocalQuaternion = parentWorldQuaternion
              .clone()
              .invert()
              .multiply(nextWorldQuaternion);
            const nextOffsetQuaternion = restLocalQuaternion
              .clone()
              .invert()
              .multiply(combinedLocalQuaternion);
            const nextOffsetEuler = sanitizeOffsetEuler(
              eulerDegreesFromQuaternion(nextOffsetQuaternion, bone.eulerOrder),
            );

            queuedOffsetUpdateRef.current = {
              boneRef: bone.internalRef,
              rotation: nextOffsetEuler,
            };
            scheduleQueuedUpdate();
          }}
        />
      )}
    </>
  );
}
